/**
 * Skin switch. Zen Spectrum is the default; Neon stays selectable, device-local.
 *
 * The choice lives in localStorage next to `spektrx-theme` and `spektrx-panel`, and NOT in `S`:
 * a skin is a property of the screen in front of you, not of the book. Keeping it out of `S`
 * keeps it out of cloud sync, merge, import/export and the secrets path.
 *
 * The whole skin is `src/styles/spektrx-zen.css`, scoped to html[data-skin="zen"]. With the
 * attribute absent only the Neon sheets paint, so Neon is the bare state of the markup and
 * every other skin is an attribute value. Which skin a device without a stored choice gets is
 * a separate question, answered by DEFAULT_SKIN.
 *
 * `index.html` carries `data-skin` for the default skin statically and `public/skin-boot.js`
 * corrects it from storage before first paint. Both must agree with the constants below; the
 * test `a-skin-is-a-property-of-the-screen` pins that.
 */
export const SKIN_KEY = 'spektrx-skin';
export const SKINS = ['neon', 'zen'];
export const DEFAULT_SKIN = 'zen';
export const BARE_SKIN = 'neon';

// A stored value we do not recognise is not a skin: it is unknown. It resolves to the default
// for display, and says so once per value (initSkin runs twice per load), rather than being
// quietly accepted as a skin name.
let _warnedFor = null;
export function readSkin() {
  let raw = null;
  try { raw = localStorage.getItem(SKIN_KEY); } catch (_e) { return DEFAULT_SKIN; }
  if (raw == null) return DEFAULT_SKIN;
  if (SKINS.includes(raw)) return raw;
  if (_warnedFor !== raw) {
    _warnedFor = raw;
    console.warn(`[ZAGX] unknown skin "${raw}" in ${SKIN_KEY}: using ${DEFAULT_SKIN}`);
  }
  return DEFAULT_SKIN;
}

export function applySkin(name) {
  const skin = SKINS.includes(name) ? name : DEFAULT_SKIN;
  const html = document.documentElement;
  if (skin === BARE_SKIN) html.removeAttribute('data-skin');
  else html.setAttribute('data-skin', skin);
  const sel = document.getElementById('sSkin');
  if (sel && sel.value !== skin) sel.value = skin;
  return skin;
}

export function setSkin(name) {
  if (!SKINS.includes(name)) {
    console.error(`[ZAGX] setSkin: "${name}" is not a skin`);
    return applySkin(readSkin());
  }
  try { localStorage.setItem(SKIN_KEY, name); } catch (_e) { /* private mode: the choice lasts for this page only */ }
  return applySkin(name);
}

export function initSkin() { return applySkin(readSkin()); }
